import classNames from "classnames";
import { isEmpty } from "lodash-es";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { ShopHead } from "@components/CommonHead";
import CommonLayout from "@components/CommonLayout";
import { Dialog } from "@components/Dialog";
import { NeighborShopList } from "@components/NeighborShopList";
import { NewsCard } from "@components/NewsCard";
import { OverlayPhoto } from "@components/OverlayPhoto";
import { PoweredByArea } from "@components/PoweredByArea";
import ShopBenefit from "@components/ShopBenefit";
import {
  BroadcastList,
  CampaignLabel,
  Description,
  EnvironmentSlide,
  Info,
  Layout,
  NameArea,
  Photo,
  VisualSlider,
} from "@components/ShopDetail";
import type { HorizontalBarChartProps } from "@components/ShopDetail/HorizontalBarChart";
import HorizontalBarChart, {
  PERCENTAGE_THRESHOLD,
} from "@components/ShopDetail/HorizontalBarChart";
import Schedule from "@components/ShopDetail/Schedule";
import StatBadge from "@components/ShopDetail/StatBadge";
import Toaster from "@components/Toaster";
import { PredefinedMessages } from "@hooks/useMessage";
import RequestReservation from "@hooks/useRequestReservation";
import { queryForSSP } from "@libs/apolloClient";
import { paths } from "@libs/paths";
import { GTMEvent, GTMEvents } from "@libs/utils/gtm";
import {
  Shop_ShopDocument as ShopDocument,
  useShop_ShopMatchQuery,
} from "./__generated__/queries";
import styles from "./index.module.css";
import type { Shop_ShopQueryResult as ShopQueryResult } from "./__generated__/queries";
import type { ApolloError } from "@apollo/client";
import type { GetServerSideProps, NextPage } from "next";

type ShopDetailProps = {
  data?: ShopQueryResult["data"];
  error?: ApolloError;
};

const PERSON_NUM_STAT_TITLE = "来店人数";
const SUPPORTER_STATS_TITLE = "観戦者の比率";

const ShopDetail: NextPage<ShopDetailProps> = ({ data, error }) => {
  const [open, setOpen] = useState(false);
  const [openOverlayPhoto, setOpenOverlayPhoto] = useState({
    open: false,
    index: 0,
  });
  const { resetRequestReservation, resetRequestReservationFramesForGA } =
    RequestReservation.useContainer();

  const { data: shopMatchData } = useShop_ShopMatchQuery({
    variables: {
      shopUuid: data?.shop.uuid ?? "",
    },
    skip: !data?.shop.uuid,
    onError: console.error,
  });

  useEffect(() => {
    resetRequestReservation();
    resetRequestReservationFramesForGA();
  }, []);

  const benefitOpenedHandler = () => {
    GTMEvent(
      GTMEvents.shopBenefitPressed(
        data?.shop.uuid ?? "",
        data?.shop.benefit?.name ?? ""
      )
    );
  };

  const chartStats = useMemo(() => {
    const stats: HorizontalBarChartProps[] = [];

    if (data?.shop.personNumStat && data.shop.personNumStat.data.length > 0) {
      stats.push({
        chartTitle: PERSON_NUM_STAT_TITLE,
        showsInProgress: false,
        chartData: data.shop.personNumStat.data.map((item) => ({
          colorCode: item.color,
          label: item.personNumText,
          value: item.percentage,
        })),
      });
    }

    if (data?.shop.supporterStats && data.shop.supporterStats.length > 0) {
      data.shop.supporterStats
        .filter((stat) => stat.data.length > 0)
        .forEach((stat) => {
          if (
            stat.data.every((item) => item.percentage < PERCENTAGE_THRESHOLD)
          ) {
            stats.push({
              chartTitle: SUPPORTER_STATS_TITLE,
              chartLabel: stat.masterTournamentCategory.label,
              showsInProgress: true,
              chartData: [],
            });
          } else {
            const rows = stat.data
              .filter((item) => item.percentage >= PERCENTAGE_THRESHOLD)
              .map((item) => ({
                colorCode: item.masterTeam.teamColor.color,
                label: item.masterTeam.shortName,
                value: item.percentage,
              }));
            const othersPercentage = stat.data
              .filter((item) => item.percentage < PERCENTAGE_THRESHOLD)
              .reduce((acc, item) => acc + item.percentage, 0);
            if (othersPercentage > 0) {
              rows.push({
                colorCode: "var(--GRAY-Base)",
                label: "その他",
                value: othersPercentage,
              });
            }
            stats.push({
              chartTitle: SUPPORTER_STATS_TITLE,
              chartLabel: stat.masterTournamentCategory.label,
              showsInProgress: false,
              chartData: rows,
            });
          }
        });
    }

    return stats;
  }, [data?.shop.personNumStat, data?.shop.supporterStats]);
  const personNumChartStat = chartStats.find(
    (stat) => stat.chartTitle === PERSON_NUM_STAT_TITLE
  );
  const supporterStatsChartStats = chartStats.filter(
    (stat) => stat.chartTitle === SUPPORTER_STATS_TITLE
  );

  const showsAnyBarChartStats = Boolean(
    personNumChartStat || supporterStatsChartStats.length > 0
  );
  const showsAnyStats = Boolean(
    (data?.shop.statBadges.length ?? 0) > 0 ||
      personNumChartStat ||
      supporterStatsChartStats.length > 0
  );

  if (!data || !shopMatchData) {
    if (error) {
      console.error(error);
    }
    if (data?.shop) {
      // c.f https://github.com/mixi-sb/fansta-doc/issues/7881
      return (
        <ShopHead
          shop={data.shop}
          overrides={{ canonicalUrl: paths.shopDetail(data.shop.uuid) }}
        />
      );
    }
    return null;
  }

  return (
    <CommonLayout hasFooter footerPC>
      <ShopHead
        shop={data.shop}
        overrides={{ canonicalUrl: paths.shopDetail(data.shop.uuid) }}
      />
      {data && (
        <div className={styles.main}>
          <Toaster
            messageFilter={[
              PredefinedMessages.requestReservationNoAvailableFramesError,
              PredefinedMessages.offlineError,
              PredefinedMessages.commonError,
              PredefinedMessages.requestReservationFailed,
              PredefinedMessages.requestReservationPlanInconsistentError,
            ]}
          />
          <div className={styles.visual}>
            <VisualSlider shopImageList={data.shop.shopImages} />
            <NameArea shop={data.shop} />
            {data.shop.statBadges.length > 0 && (
              <StatBadge badges={data.shop.statBadges} />
            )}
            {!isEmpty(data.shop.campaignLabels) && (
              <CampaignLabel campaignLabels={data.shop.campaignLabels} />
            )}
            {showsAnyBarChartStats && (
              <div className={styles.horizontalBarChartArea}>
                <div className={styles.horizontalBarChartForPc}>
                  <ul
                    className={
                      chartStats.length > 1
                        ? styles.horizontalBarChartGrid
                        : styles.horizontalBarChartSingle
                    }
                  >
                    {chartStats.map((stat, index) => (
                      <li key={index}>
                        <HorizontalBarChart
                          chartTitle={stat.chartTitle}
                          chartLabel={stat.chartLabel}
                          showsInProgress={stat.showsInProgress}
                          chartData={stat.chartData}
                        />
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={styles.horizontalBarChartForSp}>
                  {personNumChartStat && (
                    <ul className={styles.horizontalBarChartSingle}>
                      <li key={personNumChartStat.chartTitle}>
                        <HorizontalBarChart
                          chartTitle={personNumChartStat.chartTitle}
                          showsInProgress={personNumChartStat.showsInProgress}
                          chartData={personNumChartStat.chartData}
                        />
                      </li>
                    </ul>
                  )}
                  {supporterStatsChartStats.length > 0 && (
                    <ul
                      className={
                        supporterStatsChartStats.length > 1
                          ? styles.horizontalBarChartGrid
                          : styles.horizontalBarChartSingle
                      }
                    >
                      {supporterStatsChartStats.map((stat, index) => (
                        <li key={index}>
                          <HorizontalBarChart
                            chartTitle={stat.chartTitle}
                            chartLabel={stat.chartLabel}
                            showsInProgress={stat.showsInProgress}
                            chartData={stat.chartData}
                          />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <p className={styles.chartNotice}>
                  ※Fansta経由の来店実績であり、実際の店舗状況とは異なる場合があります。
                </p>
              </div>
            )}
            {showsAnyStats && (
              <div className={styles.aboutStats}>
                <Link href={paths.aboutShopStat}>表示データについて</Link>
              </div>
            )}
          </div>

          <Layout shop={data.shop} current="top" showsPoweredByArea={false}>
            {data.shop.benefit && (
              <ShopBenefit
                benefit={data.shop.benefit}
                onOpened={benefitOpenedHandler}
              />
            )}
            <section className={styles.schedule}>
              <Schedule shop={data.shop} />
              <div
                className={classNames(styles.columnInner, "l-main__innerWidth")}
              >
                <Description
                  title={data.shop.descriptionTitle}
                  body={data.shop.descriptionBody}
                />
              </div>
            </section>
            {data.shop.newsArticles.length > 0 && (
              <section className={styles.news}>
                <h1 className={`${styles.newsTitle} c-contentTitle`}>
                  ニュース
                </h1>
                <div
                  className={classNames(
                    styles.columnInner,
                    "l-main__innerWidth"
                  )}
                >
                  <div className={styles.newsList}>
                    {data.shop.newsArticles.map((newsArticle) => (
                      <NewsCard
                        newsArticle={newsArticle}
                        key={newsArticle.id}
                      />
                    ))}
                  </div>
                </div>
              </section>
            )}

            <BroadcastList
              shopUuid={data.shop.uuid}
              shopName={data.shop.name}
              shopTel={data.shop.tel}
              shopTelWithHyphen={data.shop.telWithHyphen}
              shopMatches={shopMatchData.shopMatch.nodes}
            />

            {/* 観戦環境が未登録の店舗では見出しだけが残らないようセクションごと表示しない */}
            {data.shop.viewEnvironments.length > 0 && (
              <section className={styles.environment}>
                <h1 className={`${styles.environmentTitle} c-contentTitle`}>
                  観戦環境
                </h1>

                <div className={styles.environmentCont}>
                  <EnvironmentSlide
                    viewEnvironments={data.shop.viewEnvironments}
                    onClick={(index): void =>
                      setOpenOverlayPhoto({ open: true, index })
                    }
                  />
                </div>
              </section>
            )}
            <Photo
              shopUuid={data.shop.uuid}
              shopImages={data.shop.shopImages}
              masterShopImages={data.masterShopImages}
            />
            <div
              className={styles.popupEisei}
              onClick={(): void => setOpen(true)}
            >
              <span>お店の衛生対策について</span>
            </div>
            <Info shop={data.shop} showsAboutShopStatLink={showsAnyStats} />
          </Layout>
          <NeighborShopList shopUuid={data.shop.uuid} />
          <div className="md:none">
            <PoweredByArea />
          </div>
        </div>
      )}

      <Dialog
        open={open}
        title="お店の衛生対策について"
        onClose={(): void => setOpen(false)}
      >
        {data.shop.sanitation}
      </Dialog>

      <OverlayPhoto
        defaultActiveIndex={openOverlayPhoto.index}
        open={openOverlayPhoto.open}
        photos={data.shop.viewEnvironments.map(
          ({ id, imageUrl, description }) => ({
            id,
            src: imageUrl,
            alt: `観戦環境画像${id}`,
            description,
          })
        )}
        onClose={(): void => setOpenOverlayPhoto({ open: false, index: 0 })}
      />
    </CommonLayout>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { params } = context;
  return queryForSSP({
    query: ShopDocument,
    variables: {
      shopUuid: params?.uuid || "",
    },
  });
};

export default ShopDetail;
